import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {Box, Button, Stack, TextField, Typography} from '@mui/material';
import {useEffect, useRef, useState} from 'react';
import useImageUpload from '../../../hooks/helper/useImageUpload';
import useVideoUpload from '../../../hooks/helper/useVideoUpload';

export interface AdminStudyEditorContentProps {
  content: string;
  onContentChange: (content: string) => void;
  moduleId: string;
  topic: string;
}

export default function AdminStudyEditorContent({
  content,
  onContentChange,
  moduleId,
  topic,
}: AdminStudyEditorContentProps) {
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const insertTextAtCursor = (text: string) => {
    const textarea = textFieldRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const newContent = content.substring(0, start) + text + content.substring(end);

    onContentChange(newContent);

    // Set cursor position after inserted text
    setTimeout(() => {
      const newPosition = start + text.length;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    }, 0);
  };

  const {uploadImage, handleFileSelect, uploading, error} = useImageUpload({
    moduleId,
    topic,
    onUploadComplete: (url) => {
      setUploadError(null);
      const imageMarkdown = `![Image](${url})`;
      insertTextAtCursor(imageMarkdown);
    },
    onError: (errorMsg) => {
      setUploadError(errorMsg);
    },
  });

  const {
    handleFileSelect: handleVideoFileSelect,
    handleYouTubeLink,
    handleRumbleLink,
  } = useVideoUpload({
    moduleId,
    topic,
    onUploadComplete: (markdown) => {
      setUploadError(null);
      insertTextAtCursor(markdown);
    },
    onError: (errorMsg) => {
      setUploadError(errorMsg);
    },
  });

  const handleImageButtonClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        uploadImage(target.files[0]);
      }
    };
    input.click();
  };

  // Handle paste events for YouTube and Rumble links
  useEffect(() => {
    const handlePaste = (e: Event) => {
      const clipboardEvent = e as ClipboardEvent;
      const text = clipboardEvent.clipboardData?.getData('text');
      if (text) {
        // Check if pasted text is a YouTube URL (regular videos, Shorts, or embeds)
        const youtubePattern = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([^&\n?#\/]+)/;
        if (youtubePattern.test(text)) {
          clipboardEvent.preventDefault();
          handleYouTubeLink(text);
          return;
        }
        // Check if pasted text is a Rumble URL
        const rumblePattern = /rumble\.com\/(?:embed\/|v)([^&\n?#\/]+)/;
        if (rumblePattern.test(text)) {
          clipboardEvent.preventDefault();
          void handleRumbleLink(text);
        }
      }
    };

    const textarea = textFieldRef.current;
    if (textarea) {
      textarea.addEventListener('paste', handlePaste);
      return () => {
        textarea.removeEventListener('paste', handlePaste);
      };
    }
  }, [handleYouTubeLink, handleRumbleLink]);

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" spacing={1} sx={{mb: 1}}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CloudUploadIcon />}
          onClick={handleImageButtonClick}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload Image'}
        </Button>
        {(error || uploadError) && (
          <Typography variant="caption" color="error" sx={{alignSelf: 'center'}}>
            {error || uploadError}
          </Typography>
        )}
      </Stack>
      <TextField
        inputRef={textFieldRef}
        multiline
        fullWidth
        value={content}
        onChange={(e) => onContentChange(e.target.value)}
        placeholder="Enter study content in markdown format..."
        sx={{
          flex: 1,
          '& .MuiInputBase-root': {
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            height: '100%',
            alignItems: 'flex-start',
          },
          '& .MuiInputBase-input': {
            height: '100% !important',
            overflow: 'auto !important',
          },
        }}
      />
      <input type="file" accept="image/*" onChange={handleFileSelect} style={{display: 'none'}} />
      <input type="file" accept="video/*" multiple onChange={handleVideoFileSelect} style={{display: 'none'}} />
    </Box>
  );
}
